import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, forkJoin, Observable } from "rxjs";

import { Menu, Permission } from "@common/models";
import { MenuAccessService } from "./menu-access.service";
import { MenuService } from "./menu.service";

@Injectable({ providedIn: "root" })
export class AppPermissionsService {
  private _currentPermissions: Permission[] = [];
  private _onPermissionsChanged: BehaviorSubject<any>;
  /** Indica si la carga de permisos ya se resolvió, con o sin resultados */
  private _registered: boolean = false;
  /**
   * El superusuario no depende de los accesos asignados a roles. Se establece
   * cuando AuthenticationService obtiene /api/users/logged.
   */
  private _isSuperUser: boolean = false;

  constructor(private _menuAccessService: MenuAccessService,
    private _menuService: MenuService) {
    this._onPermissionsChanged = new BehaviorSubject(null);
  }

  get onPermissionsChanged(): Observable<any> {
    return this._onPermissionsChanged.asObservable();
  }

  get currentPermissions(): Permission[]{
    return this._currentPermissions;
  }

  /** Indica si el usuario autenticado tiene acceso global */
  get isSuperUser(): boolean {
    return this._isSuperUser;
  }

  /**
   * Actualiza el estado de superusuario sin crear una dependencia circular con
   * AuthenticationService (AuthenticationService ya depende de este servicio).
   */
  setSuperUser(value: boolean): void {
    this._isSuperUser = value === true;
  }

  /**
   * Indica si la carga de permisos ya se resolvió, sin importar si el usuario
   * terminó teniendo permisos o no.
   *
   * Sirve para distinguir "los permisos todavía están cargando" de "este usuario
   * no tiene permisos", que en memoria se ven igual: la lista vacía.
   */
  get hasRegistered(): boolean {
    return this._registered;
  }

  /**
   * Register the provided permissions
   *
   */
  register() {
    this._registered = false;

    // Un superusuario necesita que el front conozca todos los menús y todos los
    // códigos de acceso. Así getMenuByUrl(), getPermissionsByMenuUrl(), etc.
    // siguen funcionando sin tener que agregar `|| user.superUser` en cada HTML.
    if (this._isSuperUser) {
      forkJoin({
        menus: this._menuService.getAll(),
        accesses: this._menuAccessService.getAllAccess()
      }).subscribe(
          ({ menus, accesses }) => {
            const response = (menus || []).map((menu) => ({
              menu,
              details: (accesses || []).map((access) => ({
                access,
                status: null
              }))
            })) as Permission[];

            this.registerResolvedPermissions(response);
          },
          () => this.registerPermissionsError()
      );

      return;
    }

    this._menuAccessService.getUserPermissions().subscribe(
      (response) => this.registerResolvedPermissions(response),
        () => this.registerPermissionsError()
    );

  }

  /** Guarda en memoria los permisos ya resueltos y notifica a los consumidores */
  private registerResolvedPermissions(response: Permission[]): void {
    this._currentPermissions = response || [];
    this._registered = true;
    this._onPermissionsChanged.next(this._currentPermissions);
  }

  /** Marca la carga como resuelta incluso cuando falle la petición */
  private registerPermissionsError(): void {
    this._currentPermissions = [];
    this._registered = true;
    this._onPermissionsChanged.next(null);
  }

  /**
   * Unregister the permissions from the registry
   *
   */
  unregister(): void {
    this._currentPermissions = [];
    this._registered = false;
    this._isSuperUser = false;

    // Notify subject
    this._onPermissionsChanged.next(null);
  }

  /**
   * Verifica si el usuario tiene accesos a la opción del menú
   * @param menuTitle
   * @param accessCode
   * @returns
   */
  hasPermission(menuTitle: string, accessCode: string): boolean {
    if (this._isSuperUser) return true;

    const permission = this._currentPermissions.filter((per) => {
      return per.menu.title === menuTitle;
    });
    if (permission.length < 1) {
      return false;
    }
    const detail = permission[0].details.filter((det) => {
      return det.access.code === accessCode;
    });
    return detail.length > 0;
  }

  /**
   * Función que devuelve si tiene permiso al menú por medio de su id
   * @param {number} menuId Indica el id del menú
   * @param {string} accessCode Indica el código de acceso al menú
   * @returns Devuelve si tiene permiso o no
   */
  hasPermissionById(menuId: number, accessCode: string): boolean {
    if (this._isSuperUser) return true;

    const permission = this._currentPermissions.filter((per) => {
      return per.menu.id === menuId;
    });
    if (permission.length < 1) {
      return false;
    }
    const detail = permission[0].details.filter((det) => {
      return det.access.code === accessCode;
    });
    return detail.length > 0;
  }

  /**
   * Función que devuelve si tiene permiso al menú por medio de la url
   * @param {string} url Indica la url del menú
   * @param {string} accessCode Indica el código de acceso al menú
   * @returns Devuelve si tiene permiso o no
   */
  hasPermissionByUrl(url: string, accessCode: string): boolean {
    if (this._isSuperUser) return true;

    const permission = this._currentPermissions.filter((per) => {
      return per.menu.link == url;
    });
    if (permission.length < 1) {
      return false;
    }
    const detail = permission[0].details.filter((det) => {
      return det.access.code === accessCode;
    });
    return detail.length > 0;
  }

  /**
   * Función que devuelve si tiene permiso al menú por medio de su id. La usan las
   * pantallas cuya ruta no coincide con el link del menú, como los sitios
   * incrustados (ahí el link guarda la url externa y la ruta es embedded/:id).
   * @param {number} menuId Indica el id del menú
   * @param {string} accessCode Indica el código de acceso al menú
   * @returns Devuelve si tiene permiso o no
   */
  hasPermissionByMenuId(menuId: number, accessCode: string): boolean {
    if (this._isSuperUser) return true;

    const permission = this._currentPermissions.filter((per) => per.menu.id === menuId);
    if (permission.length < 1) {
      return false;
    }
    return permission[0].details.filter((det) => det.access.code === accessCode).length > 0;
  }

  /**
   * Función que devuelve si tiene permiso a las urls
   * @param {string[]} urls Indica las urls que se validará para ver si tiene el acceso
   * @param {string} accessCode Indica el código de acceso al menú
   * @returns Deuvelve si tiene permiso o no
   */
  hasPermissionByUrls(urls: string[], accessCode: string): boolean {
    if (this._isSuperUser) return true;

    // Obtiene los permisos por medio de las urls
    const permissions = this._currentPermissions.filter((per) => {
      // Realiza una filtración por medio de las urls y el detalle
      return urls.includes(per.menu.link) 
        && per.details.filter(detail => detail.access.code === accessCode).length > 0;
    });

    // Si es mayor a 1 significa que tiene permiso
    return permissions.length > 0;
  }

  /**
   * Obtiene el menú por id
   * @param {number} menuId Indica el id del menú
   * @returns {Menu} Devuelve un objeto de tipo Menu
   */
  getMenuById(menuId: number): Menu {
    // Busca el menú por medio del titulo del menú, dentro de los permisos del usuario
    const permissions = this._currentPermissions.filter(
      (per) => per.menu.id === menuId
    );

    // Devuelve el menú que encuentre dentro de los permisos del usuario
    return permissions?.length > 0 ? permissions[0].menu : null;
  }

  /**
   * Busca el menú por medio del titulo del menú dentro de la lista de permisos del usuario
   * @param {string} menuTitle Indica el titulo del menú a buscar
   * @returns {Menu} Devuelve el menú encontrado
   */
  getMenuByTitle(menuTitle: string): Menu {
    // Busca el menú por medio del titulo del menú, dentro de los permisos del usuario
    const permissions = this._currentPermissions.filter(
      (per) => per.menu.title === menuTitle
    );

    // Devuelve el menú que encuentre dentro de los permisos del usuario
    return permissions?.length > 0 ? permissions[0].menu : null;
  }

  /**
   * Función que devuelve un map de permisos del usuario por menú
   * @param {string} menuTitle Indica el titulo del menú
   * @returns Devuelve un map con los accesos
   */
  getPermissionsByMenuTitle(menuTitle: string): {} {

    // Busca el menú por medio del titulo del menú, dentro de los permisos del usuario
    const permissions = this._currentPermissions.filter(
      (per) => per.menu.title === menuTitle
    );

    // Inicializa el map de accesos
    let access = {};

    // Verifica si tiene permisos
    if (permissions && permissions.length > 0){
      // Asigna los accesos que tiene el usuario
      permissions[0].details
        .forEach((permission) => access[permission.access.code.toLowerCase()] = true);
    }

    // Devuelve el menú que encuentre dentro de los permisos del usuario
    return access;
  }

  /**
   * Función que devuelve un map de permisos del usuario por menú
   * @param {string} menuUrl Indica la url del menú
   * @returns Devuelve un map con los accesos
   */
  getPermissionsByMenuUrl(menuUrl: string): {} {

    // Busca el menú por medio del titulo del menú, dentro de los permisos del usuario
    const permissions = this._currentPermissions.filter(
      (per) => per.menu.link === menuUrl
    );

    // Inicializa el map de accesos
    let access = {};

    // Verifica si tiene permisos
    if (permissions && permissions.length > 0){
      // Asigna los accesos que tiene el usuario
      permissions[0].details
        .forEach((permission) => access[permission.access.code.toLowerCase()] = true);
    }

    // Devuelve el menú que encuentre dentro de los permisos del usuario
    return access;
  }

  /**
   * Busca el menú por medio de la url del menú dentro de la lista de permisos del usuario
   * @param {string} link Indica la url del menú a buscar
   * @returns {Menu} Devuelve el menú encontrado
   */
  getMenuByUrl(link: string): Menu {
    // Busca el menú por medio de la url del menú, dentro de los permisos del usuario
    const permissions = this._currentPermissions.filter(
      (per) => per.menu.link === link
    );

    // Devuelve el menú que encuentre dentro de los permisos del usuario
    return permissions?.length > 0 ? permissions[0].menu : null;
  }

  /**
   * Función que indica si ya están registrados los permisos
   * @returns {boolean} Devuelve si ya tiene items de permisos o no cargados
   */
  hasPermissionsItems(): boolean {
    return this._currentPermissions && this._currentPermissions.length > 0;
  }

}
